// Yandex Metrica integration for the landing site.
//
// The tag id comes from VITE_YANDEX_METRIKA_ID (baked in at build time). When it is
// unset — local dev, staging, or any build without the env var — every function here is
// a no-op, so only real production traffic is ever counted.
//
// SSG note: vite-react-ssg prerenders on Node, where `window`/`document` don't exist.
// Every entry point guards on that, and the counter is injected from a React effect
// (which never runs during prerender), so the generated static HTML stays clean.

const YM_ID = (import.meta.env.VITE_YANDEX_METRIKA_ID as string | undefined)?.trim();

// `ym` is a tiny queue function until Metrica's tag.js loads and replaces it. It also
// carries `.a` (buffered calls) and `.l` (load timestamp), mirroring the official snippet.
interface YmFn {
  (...args: unknown[]): void;
  a?: unknown[][];
  l?: number;
}
declare global {
  interface Window {
    ym?: YmFn;
  }
}

/** True only when a tag id is configured — gates every call so dev/staging stay untracked. */
export const metrikaEnabled = Boolean(YM_ID);

// Production host allow-list. Metrica must run ONLY on the real prod domain — the same
// Dockerfile builds the staging image (nasiya/landing:staging) from the same source, so a
// baked-in counter id could otherwise leak analytics from staging.nasiyah.uz / previews /
// localhost. This runtime gate keeps every non-prod host untracked regardless of the build.
const PROD_HOSTNAMES = new Set(["nasiyah.uz", "www.nasiyah.uz"]);
function onProductionHost(): boolean {
  return typeof window !== "undefined" && PROD_HOSTNAMES.has(window.location.hostname);
}

// Named conversion goals. Create goals with these exact ids in the Metrica dashboard
// (Goals → "JavaScript event") to count how many visitors reach each action.
export const GOALS = {
  startApp: "start_app", // primary "Bepul boshlash" CTA → opens the store
  downloadApp: "download_app", // App Store / Google Play badge click
  telegram: "telegram", // Telegram contact link
} as const;
export type Goal = (typeof GOALS)[keyof typeof GOALS];

// Last URL we logged a pageview for. Module-level (survives React layout remounts) so the
// initial hit fired by `init` is never duplicated by the first route effect.
let lastTrackedUrl: string | null = null;

/** Inject the counter once and log the initial pageview. Safe to call repeatedly. */
export function initMetrika(): void {
  if (!YM_ID || typeof window === "undefined" || typeof document === "undefined") return;
  if (!onProductionHost()) return; // staging.nasiyah.uz / previews / localhost stay untracked
  if (window.ym) return; // already initialised

  const id = Number(YM_ID);

  // Bootstrap the `ym` queue, then load tag.js (mirrors Metrica's official snippet).
  const queue: YmFn = Object.assign(
    function (...args: unknown[]) {
      (queue.a = queue.a || []).push(args);
    },
    { a: [] as unknown[][], l: new Date().getTime() },
  );
  window.ym = queue;

  const src = "https://mc.yandex.ru/metrika/tag.js";
  if (!Array.from(document.scripts).some((s) => s.src === src)) {
    const script = document.createElement("script");
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
  }

  window.ym(id, "init", {
    clickmap: true, // heatmap of where visitors click
    trackLinks: true, // log outbound (store / Telegram) link clicks
    accurateTrackBounce: true,
    webvisor: true, // session replay
  });

  // `init` already counts the current page — remember it so the first route effect skips it.
  lastTrackedUrl = window.location.href;
}

/** Log a single-page-app pageview. De-duplicates against the last URL (initial hit + remounts). */
export function trackHit(url: string): void {
  if (!YM_ID || typeof window === "undefined" || !window.ym || !onProductionHost()) return;
  if (url === lastTrackedUrl) return;
  lastTrackedUrl = url;
  window.ym(Number(YM_ID), "hit", url);
}

/** Record a conversion goal (see GOALS). No-op unless the counter is configured + loaded. */
export function reachGoal(goal: Goal): void {
  if (!YM_ID || typeof window === "undefined" || !window.ym || !onProductionHost()) return;
  window.ym(Number(YM_ID), "reachGoal", goal);
}
